import { MimeType_json, Module, MUSTNeverHappenException, sortArray } from '@nu-art/ts-common';
import { ModuleFE_AdvisorSession, ModuleFE_EMInterface, StorageKey_SnapShotVersion } from '@app/advisor-frontend/index';
import { ModuleFE_SyncManager_CSV, ModuleFE_XHR } from '@nu-art/thunderstorm-frontend';
import { ApiDef_ArtifactSyncer } from '@app/advisor-shared';
import { HeaderKey_ContentType } from '@nu-art/thunderstorm-shared';
import { ModuleFE_AdvisorAnalytics } from '../../_analytics/consts.js';
class ModuleFE_AdvisorSync_Class extends Module {
    //########################## Internal Logic ##########################
    getArtifactGroups = (artifactData) => {
        //Operating with legacy single artifact url
        if (!artifactData.advisorArtifactSignedUrls)
            return [{
                    groupPriorityIndex: 0,
                    artifacts: [{ key: 'single', url: artifactData.advisorArtifactSignedUrl }]
                }];
        const groups = [];
        Object.entries(artifactData.advisorArtifactSignedUrls).forEach(entry => {
            const [key, item] = entry;
            let group = groups.find(i => i.groupPriorityIndex === item.priority);
            if (!group) {
                group = { groupPriorityIndex: item.priority, artifacts: [] };
                groups.push(group);
            }
            group.artifacts.push({ key, url: item.signedUrl });
        });
        return sortArray(groups, 'groupPriorityIndex');
    };
    syncArtifact = (artifact, priorityIndex) => {
        const path = ModuleFE_EMInterface.config.artifactLoaderEndpoint + ApiDef_ArtifactSyncer.getArtifact.path;
        return new Promise((resolve, reject) => {
            const start = Date.now();
            ModuleFE_SyncManager_CSV.syncFromCSVUrl(path, {
                downloadRequestHeaders: {
                    ...ModuleFE_XHR.getDefaultHeaders(),
                    [HeaderKey_ContentType]: MimeType_json,
                },
                downloadRequestBody: JSON.stringify({ pathToFile: artifact.url })
            })
                .then(() => {
                const end = Date.now();
                ModuleFE_AdvisorAnalytics.trigger.uiLoad.uiLoad_artifact({
                    artifactKey: artifact.key,
                    priorityIndex,
                    downloadTime: end - start,
                });
                resolve();
            })
                .catch(err => reject(err));
        });
    };
    syncArtifactGroup = async (artifactGroup) => {
        this.logInfo(`Syncing group priority index: ${artifactGroup.groupPriorityIndex}`);
        await Promise.all(artifactGroup.artifacts.map(artifact => this.syncArtifact(artifact, artifactGroup.groupPriorityIndex)))
            .then(() => this.logInfo(`Synced ${artifactGroup.artifacts.length} csv(s)`))
            .catch(err => {
            this.logError('Failed syncing artifact group:', err);
            throw err;
        });
    };
    //########################## Public Interface ##########################
    syncSnapshot = async () => {
        try {
            const currentVersion = StorageKey_SnapShotVersion.get();
            const artifactData = ModuleFE_AdvisorSession.getSessionLaunchData();
            if (!artifactData.dataVersion || !artifactData.advisorArtifactSignedUrl)
                throw new MUSTNeverHappenException('cannot sync artifact without necessary data');
            if (artifactData.dataVersion === currentVersion) {
                this.logInfo(`Will not sync snapshot - same version: ${currentVersion}`);
                // Probe IDB BEFORE readying modules. readyAllModules() flips every CSVSync
                // module (incl. AppConfig + FindingView) to ContainsData, which synchronously
                // opens their AwaitModules gates. If IDB was evicted while the version key
                // survived, readying first would render content against empty caches and crash
                // (getInitialPanelState -> topLevelViews[0].type). hasAnyData() reads
                // IDB.count() without touching statuses, so it is safe to check first.
                if (await ModuleFE_SyncManager_CSV.hasAnyData()) {
                    await ModuleFE_SyncManager_CSV.readyAllModules();
                    return;
                }
                this.logWarning('Snapshot version present but IDB is empty — re-syncing');
                StorageKey_SnapShotVersion.delete();
                // fall through to a full sync
            }
            this.logInfo(`Will sync snapshot - version: ${artifactData.dataVersion}`);
            const artifactGroups = this.getArtifactGroups(artifactData);
            for (const group of artifactGroups)
                await this.syncArtifactGroup(group);
            //Ready all modules that weren't initialized by sync
            await ModuleFE_SyncManager_CSV.readyAllUnreadyModules();
            // Write the version marker ONLY after a full sync completes, so the key is a
            // completion sentinel rather than a write-ahead intent. An interrupted sync now
            // leaves no key and re-syncs on next launch (syncIndexDb is upsert-only, so
            // re-syncing over a partial IDB is additive and safe).
            StorageKey_SnapShotVersion.set(artifactData.dataVersion);
        }
        catch (err) {
            this.logError('error syncing snapshot', err);
            StorageKey_SnapShotVersion.delete();
            throw err;
        }
    };
}
export const ModuleFE_AdvisorSync = new ModuleFE_AdvisorSync_Class();
//# sourceMappingURL=ModuleFE_AdvisorSync.js.map